import {
  ArrowPathIcon,
  CheckCircleIcon,
  ClockIcon,
  CreditCardIcon,
  DocumentTextIcon,
  EnvelopeIcon,
  ExclamationTriangleIcon,
  TruckIcon,
  XCircleIcon,
} from '@heroicons/react/16/solid';
import { useState } from 'react';

import { StatusBadge } from '@/components/postgrid/common/PostgridStatusBadge';
import type {
  ChequeSchema,
  LetterSchema,
  PostGridEventStatus,
  PostGridEventType,
  PostGridObjectStatus,
  ReadPostgridEvent,
} from '@/services/api/generated/postgridService';

interface EventTimelineProps {
  events: ReadPostgridEvent[];
  isLoading?: boolean;
  emptyMessage?: string;
}

const getEventIcon = (
  eventType: PostGridEventType | undefined,
  processingStatus: PostGridEventStatus,
) => {
  // Processing status icons take precedence
  switch (processingStatus) {
    case 'failed':
      return <XCircleIcon className="w-4 h-4 text-red-500" />;
    case 'retrying':
      return <ArrowPathIcon className="w-4 h-4 text-yellow-500" />;
    case 'processed':
      return <CheckCircleIcon className="w-4 h-4 text-green-500" />;
    case 'processing':
      return <ClockIcon className="w-4 h-4 text-blue-500" />;
    case 'pending':
      return <ClockIcon className="w-4 h-4 text-gray-500" />;
    default:
      break;
  }

  // Event type icons
  if (!eventType) {
    return <EnvelopeIcon className="w-4 h-4 text-gray-500" />;
  }

  if (eventType.includes('letter')) {
    return <DocumentTextIcon className="w-4 h-4 text-indigo-500" />;
  }
  if (eventType.includes('cheque')) {
    return <CreditCardIcon className="w-4 h-4 text-blue-500" />;
  }
  if (eventType.includes('in_transit') || eventType.includes('delivery')) {
    return <TruckIcon className="w-4 h-4 text-green-500" />;
  }

  return <EnvelopeIcon className="w-4 h-4 text-gray-500" />;
};

const formatEventTitle = (eventType: PostGridEventType | undefined) => {
  if (!eventType) {
    return 'Unknown Event';
  }

  const eventTitles: Record<PostGridEventType, string> = {
    'letter.created': 'Letter Created',
    'letter.updated': 'Letter Updated',
    'letter.ready': 'Letter Ready',
    'cheque.created': 'Cheque Created',
    'cheque.updated': 'Cheque Updated',
    'cheque.ready': 'Cheque Ready',
    'processing.failed': 'Processing Failed',
    'general.error': 'Processing Error',
    'postcard.created': 'Postcard Created',
    'postcard.updated': 'Postcard Updated',
    'self_mailer.created': 'Self Mailer Created',
    'self_mailer.updated': 'Self Mailer Updated',
    'box.created': 'Box Created',
    'box.updated': 'Box Updated',
    'return_envelope_order.created': 'Return Envelope Order Created',
    'return_envelope_order.updated': 'Return Envelope Order Updated',
    'tracker.visited': 'Tracker Visited',
    'campaign.created': 'Campaign Created',
    'campaign.updated': 'Campaign Updated',
  };

  return (
    eventTitles[eventType] ||
    eventType.replace(/[._]/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase())
  );
};

const formatAmount = (amount: number): string => {
  const dollars =
    Number.isInteger(amount) && amount >= 1000 ? amount / 100 : amount;
  return `$${dollars.toFixed(2)}`;
};

const formatStatus = (status: string): string =>
  status.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase());

const getEventDetails = (event: ReadPostgridEvent): string => {
  const { event_data } = event;

  // Extract key info from event data
  if (event_data?.data && typeof event_data.data === 'object') {
    // Handle cheque data
    if (event_data.data.object === 'cheque') {
      const data = event_data.data as ChequeSchema & { object: 'cheque' };

      if (data.status === 'delivered') {
        return `Delivered - ${formatAmount(data.amount)}`;
      }
      if (data.status === 'in_transit' && data.trackingNumber) {
        return `In transit - ${data.trackingNumber}`;
      }
      if (data.status === 'returned_to_sender') {
        return 'Returned - verify address';
      }
      if (data.trackingNumber) {
        return `Tracking: ${data.trackingNumber}`;
      }
      if (data.imbStatus) {
        return `IMB: ${formatStatus(data.imbStatus)}`;
      }
      if (data.amount) {
        return formatAmount(data.amount);
      }
      if (data.status) {
        return formatStatus(data.status);
      }
    }

    // Handle letter data
    if (event_data.data.object === 'letter') {
      const data = event_data.data as LetterSchema & { object: 'letter' };

      if (data.status === 'delivered') {
        return 'Delivered';
      }
      if (data.status === 'in_transit' && data.trackingNumber) {
        return `In transit - ${data.trackingNumber}`;
      }
      if (data.status === 'returned_to_sender') {
        return 'Returned - verify address';
      }
      if (data.trackingNumber) {
        return `Tracking: ${data.trackingNumber}`;
      }
      if (data.imbStatus) {
        return `IMB: ${formatStatus(data.imbStatus)}`;
      }
      if (data.status) {
        return formatStatus(data.status);
      }
    }
  }

  return '';
};

export const EventTimeline = ({
  events,
  isLoading = false,
  emptyMessage = 'No delivery events yet',
}: EventTimelineProps) => {
  const [showAll, setShowAll] = useState(false);

  const formatDate = (dateString: string | null | undefined): string => {
    if (!dateString) {
      return 'Unknown date';
    }

    try {
      const date = new Date(dateString);
      if (Number.isNaN(date.getTime())) {
        return 'Invalid date';
      }

      return date.toLocaleString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch {
      return 'Invalid date';
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-4">
        <div className="text-sm text-gray-500">Loading events...</div>
      </div>
    );
  }

  if (events.length === 0) {
    return (
      <div className="text-center py-6 text-gray-500">
        <ClockIcon className="w-6 h-6 mx-auto mb-2 text-gray-400" />
        <p className="text-sm">{emptyMessage}</p>
      </div>
    );
  }

  // Sort events by occurred_at (most recent first)
  const sortedEvents = [...events].sort((a, b) => {
    const dateA = new Date(a.occurred_at || a.created_at).getTime();
    const dateB = new Date(b.occurred_at || b.created_at).getTime();
    return dateB - dateA;
  });

  // Show only first 3 by default, all if expanded
  const displayEvents = showAll ? sortedEvents : sortedEvents.slice(0, 3);
  const hasMore = sortedEvents.length > 3;

  return (
    <div className="space-y-3">
      {displayEvents.map((event, index) => {
        const details = getEventDetails(event);
        const objectStatus = (
          event.event_data?.data as { status?: PostGridObjectStatus }
        )?.status;
        const isLastEvent = index === displayEvents.length - 1;

        return (
          <div
            key={event.id || index}
            className="relative flex items-start space-x-3"
          >
            <div className="flex-shrink-0 mt-1">
              {getEventIcon(event.event_type, event.processing_status)}
            </div>

            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between mb-1">
                <div className="flex items-center gap-2">
                  <p className="text-sm font-medium text-gray-900">
                    {formatEventTitle(event.event_type)}
                  </p>
                  {objectStatus && <StatusBadge status={objectStatus} />}
                </div>
                <p className="text-xs text-gray-500">
                  {formatDate(event.occurred_at || event.created_at)}
                </p>
              </div>

              {details && (
                <p className="text-sm text-gray-600 mb-1">{details}</p>
              )}

              {event.processing_error && (
                <div className="flex items-start gap-2 mt-2 p-2 bg-red-50 rounded border border-red-200">
                  <ExclamationTriangleIcon className="w-4 h-4 text-red-500 mt-0.5 flex-shrink-0" />
                  <p className="text-sm text-red-700">
                    {event.processing_error}
                  </p>
                </div>
              )}
            </div>

            {!isLastEvent && (
              <div className="absolute left-2 top-6 w-px h-6 bg-gray-200 -ml-px" />
            )}
          </div>
        );
      })}

      {hasMore && (
        <div className="pt-2">
          <button
            type="button"
            onClick={() => setShowAll(!showAll)}
            className="text-sm text-blue-600 hover:text-blue-800 font-medium"
          >
            {showAll
              ? `Show less (${sortedEvents.length - 3} events hidden)`
              : `Show all ${sortedEvents.length} events`}
          </button>
        </div>
      )}
    </div>
  );
};
